/**
 * MonteCarloPage — Stochastic FIRE simulator
 *
 * Runs 10,000 portfolio simulations with random annual returns drawn
 * from a normal distribution. Shows probability of success, fan chart
 * of percentile bands, and key risk metrics.
 */
import { useEffect, useRef, useState } from 'react'
import {
  Sparkles, RefreshCw, TrendingUp, AlertTriangle, Info,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts'
import { api } from '@/lib/api'
import ScenariosTabs from '@/components/ui/ScenariosTabs'
import { usePlanningAssumptions } from '@/hooks/usePlanningAssumptions'
import ScenarioDisclaimerFooter from '@/components/ScenarioDisclaimerFooter'
import EditableNumberSlider from '@/components/ui/EditableNumberSlider'
import { fmtGBP, fmtGBPCompact, fmtNum, fmtPct } from '@/lib/format'
import { gridStroke, axisTickFill, tooltipContentStyle } from '@/lib/chartTheme'

interface FanPoint {
  year: number
  age: number
  p10: number
  p25: number
  p50: number
  p75: number
  p90: number
}

interface MonteCarloResult {
  success_probability_pct: number
  median_final_gbp: number
  percentile_10_final_gbp: number
  percentile_90_final_gbp: number
  median_path: FanPoint[]
  depletion_age_median: number | null
  worst_drawdown_pct: number
  summary_headline: string
  inputs: {
    household_gbp: number
    annual_spend_gbp: number
    current_age: number
    retirement_age: number
    age_target: number
    mean_real_return_pct: number
    volatility_pct: number
    num_simulations: number
  }
  // Optional FX-stress metadata — only present when fx_stress_pct != 0
  fx_stress_applied_pct?: number | null
  fx_foreign_share?: number | null
  pot_before_fx?: number | null
  pot_after_fx?: number | null
  fx_stress_note?: string | null
}

// FX-stress preset options — mirror the DrawdownPage presets for consistency
const FX_PRESETS: { label: string; value: number }[] = [
  { label: '-15%', value: -15 },
  { label: '-5%', value: -5 },
  { label: 'None', value: 0 },
  { label: '+5%', value: 5 },
  { label: '+15%', value: 15 },
]

function successColour(pct: number): string {
  if (pct >= 90) return 'text-emerald-light border-emerald/40 bg-emerald/5'
  if (pct >= 70) return 'text-amber border-amber/40 bg-amber/5'
  return 'text-rose-light border-rose/40 bg-rose/5'
}

function successBadge(pct: number): string {
  if (pct >= 90) return 'badge-green'
  if (pct >= 70) return 'badge-amber'
  return 'badge-red'
}

export default function MonteCarloPage() {
  const [spend, setSpend] = useState(220_000)
  const [volatility, setVolatility] = useState(12)
  const [numSims, setNumSims] = useState(10_000)
  const [meanReturn, setMeanReturn] = useState(5.0)
  const [currentAge, setCurrentAge] = useState(57)
  const [retireAge, setRetireAge] = useState(60)
  const [ageTarget, setAgeTarget] = useState(95)

  const [fxStressPct, setFxStressPct] = useState(0)

  const [data, setData] = useState<MonteCarloResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Phase A — shared planning assumptions (single source of truth across
  // MC / FIRE / drawdown / scenarios). On mount we hydrate local state from
  // the resolved view; slider changes write back through the store.
  const assumptions = usePlanningAssumptions((s) => s.assumptions)
  const fetchAssumptions = usePlanningAssumptions((s) => s.fetch)
  const patchDebounced = usePlanningAssumptions((s) => s.patchDebounced)
  const hydratedRef = useRef(false)

  useEffect(() => {
    if (!assumptions) {
      void fetchAssumptions()
      return
    }
    // One-shot hydration: when the store first delivers values, sync our
    // local slider state. Subsequent profile changes from other pages
    // arrive via the store and don't overwrite in-progress local edits.
    if (!hydratedRef.current) {
      hydratedRef.current = true
      setSpend(assumptions.target_drawdown_gbp ?? 220_000)
      setMeanReturn(assumptions.mean_real_return_pct)
      setVolatility(assumptions.volatility_pct)
      setCurrentAge(assumptions.current_age)
      setRetireAge(assumptions.retirement_age)
    }
  }, [assumptions, fetchAssumptions])

  const runSimulation = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.post<MonteCarloResult>('/api/planning/monte-carlo', {
        annual_spend_gbp: spend,
        current_age: currentAge,
        retirement_age: retireAge,
        age_target: ageTarget,
        // Omitted deliberately: the backend reads annual_savings_gbp from the
        // household profile when it isn't supplied. Sending a hardcoded 0 (the
        // old behaviour) wiped out every year of pre-retirement saving and
        // understated success odds against the FIRE planner's own numbers.
        mean_real_return_pct: meanReturn,
        volatility_pct: volatility,
        num_simulations: numSims,
        fx_stress_pct: fxStressPct,
      })
      setData(res.data)
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? err?.message ?? 'Simulation failed')
    } finally {
      setLoading(false)
    }
  }

  // Initial load
  useEffect(() => {
    runSimulation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-run on input change with debounce
  useEffect(() => {
    const t = setTimeout(runSimulation, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spend, volatility, numSims, meanReturn, currentAge, retireAge, ageTarget, fxStressPct])

  const successPct = data?.success_probability_pct ?? 0

  // Fan chart data: stack bands as deltas for AreaChart
  const chartData = (data?.median_path ?? []).map(p => ({
    age: p.age,
    // Band spans: p10, p25-p10, p50-p25, p75-p50, p90-p75
    band_p10: p.p10,
    band_p10_p25: Math.max(0, p.p25 - p.p10),
    band_p25_p50: Math.max(0, p.p50 - p.p25),
    band_p50_p75: Math.max(0, p.p75 - p.p50),
    band_p75_p90: Math.max(0, p.p90 - p.p75),
    p50: p.p50,
  })) ?? []

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Sparkles size={20} className="text-blue-light" aria-hidden />
            Monte Carlo FIRE Simulator
          </h1>
          <p className="text-sm text-text-secondary">
            10,000 stochastic paths — probability of financial independence to age {ageTarget}
          </p>
        </div>
        <button
          type="button"
          onClick={runSimulation}
          disabled={loading}
          className="btn-primary gap-2 text-xs"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          {loading ? 'Simulating…' : 'Re-run'}
        </button>
      </div>

      <ScenariosTabs />

      {error && (
        <div className="card p-4 border-rose/30 bg-rose/5 text-rose-light text-sm">
          {error}
        </div>
      )}

      {/* Inputs */}
      <div className="card p-5">
        <h2 className="panel-title mb-4 flex items-center gap-2">
          <Sparkles size={14} className="text-blue-light" aria-hidden />
          Simulation Inputs
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <EditableNumberSlider
            label="Annual drawdown (gross)"
            value={spend}
            onChange={(v) => { setSpend(v); patchDebounced({ target_drawdown_gbp: v }) }}
            min={50_000}
            max={400_000}
            step={5_000}
            format={fmtGBP}
          />
          <NumberSlider
            label="Return volatility (σ)"
            value={volatility}
            onChange={(v) => { setVolatility(v); patchDebounced({ volatility_pct: v }) }}
            min={5}
            max={25}
            step={1}
            format={(v) => `${v}%`}
          />
          <NumberSlider
            label="Mean real return"
            value={meanReturn}
            onChange={(v) => { setMeanReturn(v); patchDebounced({ mean_real_return_pct: v }) }}
            min={1}
            max={10}
            step={0.5}
            format={(v) => `${v.toFixed(1)}%`}
          />
          <NumberSlider
            label="Current age"
            value={currentAge}
            onChange={(v) => { setCurrentAge(v); patchDebounced({ current_age: v }) }}
            min={25}
            max={80}
            step={1}
            format={(v) => `${v}`}
          />
          <NumberSlider
            label="Retirement age"
            value={retireAge}
            onChange={(v) => { setRetireAge(v); patchDebounced({ retirement_age: v }) }}
            min={currentAge}
            max={80}
            step={1}
            format={(v) => `${v}`}
          />
          <NumberSlider
            label="Planning horizon (age)"
            value={ageTarget}
            onChange={setAgeTarget}
            min={70}
            max={110}
            step={1}
            format={(v) => `${v}`}
          />
          <NumberSlider
            label="Simulations"
            value={numSims}
            onChange={setNumSims}
            min={1_000}
            max={20_000}
            step={1_000}
            format={(v) => v.toLocaleString()}
          />
        </div>
      </div>

      {/* FX Stress Control */}
      <div className="card p-5">
        <h2 className="panel-title mb-1 flex items-center gap-2">
          <AlertTriangle size={14} className="text-amber" aria-hidden />
          FX Stress — Sustained GBP Move
        </h2>
        <p className="text-xs text-text-muted mb-3">
          A USD-heavy book overstates success probability when FX is assumed static.
          Select a sustained GBP move to stress-test the starting pot — only the
          foreign-currency share of your portfolio is affected.
        </p>
        <div className="flex flex-wrap gap-2" role="group" aria-label="FX stress preset">
          {FX_PRESETS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => setFxStressPct(preset.value)}
              className={clsx(
                'px-3 py-1.5 rounded text-xs font-semibold border transition-colors',
                fxStressPct === preset.value
                  ? preset.value > 0
                    ? 'bg-rose/20 border-rose/50 text-rose-light'
                    : preset.value < 0
                    ? 'bg-emerald/20 border-emerald/50 text-emerald-light'
                    : 'bg-blue/20 border-blue/50 text-blue-light'
                  : 'border-border text-text-muted hover:border-blue/40 hover:text-text-secondary',
              )}
              aria-pressed={fxStressPct === preset.value ? true : false}
            >
              {preset.label}
              {preset.value > 0 && ' GBP↑'}
              {preset.value < 0 && ' GBP↓'}
              {preset.value === 0 && ' (default)'}
            </button>
          ))}
        </div>
        {/* FX stress impact — shown only when stress applied and data available */}
        {fxStressPct !== 0 && data?.fx_stress_applied_pct != null && (
          <div className="mt-3 p-3 rounded bg-amber/5 border border-amber/20 text-xs text-text-secondary space-y-1">
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              <span>
                <span className="text-text-muted">Foreign share:</span>{' '}
                <span className="font-semibold text-text-primary">
                  {fmtPct((data.fx_foreign_share ?? NaN) * 100, 1)}
                </span>
              </span>
              <span>
                <span className="text-text-muted">Pot before stress:</span>{' '}
                <span className="font-semibold text-text-primary">{fmtGBP(data.pot_before_fx ?? null)}</span>
              </span>
              <span>
                <span className="text-text-muted">Pot after stress:</span>{' '}
                <span className={clsx(
                  'font-semibold',
                  (data.pot_after_fx ?? 0) < (data.pot_before_fx ?? 0) ? 'text-rose-light' : 'text-emerald-light',
                )}>
                  {fmtGBP(data.pot_after_fx ?? null)}
                </span>
              </span>
            </div>
            {data.fx_stress_note && (
              <p className="text-text-muted">{data.fx_stress_note}</p>
            )}
            <p className="text-text-muted italic">
              Success probability above reflects this stressed starting pot.
            </p>
          </div>
        )}
      </div>

      {/* Big headline card */}
      {data && (
        <div className={clsx('card p-6 border-2', successColour(successPct))}>
          <div className="flex items-start gap-6 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="panel-title opacity-70">
                Monte Carlo Result — {data.inputs.num_simulations.toLocaleString()} simulations
              </p>
              <h2 className="text-2xl font-bold text-text-primary mt-1">
                {data.summary_headline}
              </h2>
              <p className="text-xs text-text-muted mt-2">
                Starting from {fmtGBP(data.inputs.household_gbp)} FIRE-applicable wealth,
                drawing {fmtGBP(data.inputs.annual_spend_gbp)}/yr (flat in today's money),
                {data.inputs.mean_real_return_pct}% mean real return ± {data.inputs.volatility_pct}% vol.
              </p>
            </div>
            {/* Giant probability dial */}
            <div className="text-center shrink-0">
              <div className={clsx('stat-hero', successPct >= 90 ? 'text-emerald-light' : successPct >= 70 ? 'text-amber' : 'text-rose-light')}>
                {fmtNum(successPct, 0)}
                <span className="text-3xl">%</span>
              </div>
              <p className="text-xs text-text-muted mt-1">success rate</p>
              <span className={clsx('badge text-[10px] mt-2', successBadge(successPct))}>
                {successPct >= 90 ? 'STRONG' : successPct >= 70 ? 'MODERATE' : 'AT RISK'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* KPI strip */}
      {data && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 stagger-children">
          <div className="stat-card">
            <p className="stat-label">Median final balance</p>
            <p className="stat-value text-blue-light">{fmtGBPCompact(data.median_final_gbp)}</p>
            <p className="text-xs text-text-muted">50th percentile at age {ageTarget}</p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Bad case (p10)</p>
            <p className={clsx('stat-value', data.percentile_10_final_gbp > 0 ? 'text-amber' : 'text-rose-light')}>
              {data.percentile_10_final_gbp > 0 ? fmtGBPCompact(data.percentile_10_final_gbp) : 'Depleted'}
            </p>
            <p className="text-xs text-text-muted">1 in 10 paths ends here</p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Good case (p90)</p>
            <p className="stat-value text-emerald-light">{fmtGBPCompact(data.percentile_90_final_gbp)}</p>
            <p className="text-xs text-text-muted">1 in 10 paths exceeds this</p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Worst drawdown (median path)</p>
            <p className={clsx('stat-value', data.worst_drawdown_pct > 40 ? 'text-rose-light' : data.worst_drawdown_pct > 20 ? 'text-amber' : 'text-text-primary')}>
              {fmtPct(data.worst_drawdown_pct, 1)}
            </p>
            <p className="text-xs text-text-muted">peak-to-trough on p50 path</p>
          </div>
        </div>
      )}

      {/* Fan chart */}
      {chartData.length > 0 && data && (
        <div className="card p-5">
          <h2 className="panel-title mb-1 flex items-center gap-2">
            <TrendingUp size={14} className="text-blue-light" aria-hidden />
            Portfolio Fan Chart — Percentile Bands
          </h2>
          <p className="text-xs text-text-muted mb-4">
            Each band shows the range of outcomes across {data.inputs.num_simulations.toLocaleString()} simulated paths.
            The dark centre line is the median (p50). The lightest band spans p10–p90.
            Assumes {data.inputs.mean_real_return_pct}% real return ± {data.inputs.volatility_pct}% annual volatility.
          </p>
          <ResponsiveContainer width="100%" height={360}>
            <AreaChart data={chartData} stackOffset="none">
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis
                dataKey="age"
                stroke={axisTickFill}
                tick={{ fontSize: 11 }}
                label={{ value: 'Age', position: 'insideBottom', offset: -5, fill: axisTickFill }}
              />
              <YAxis
                stroke={axisTickFill}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => fmtGBPCompact(v)}
              />
              <Tooltip
                contentStyle={tooltipContentStyle}
                formatter={(v: any, name: string) => {
                  const labels: Record<string, string> = {
                    band_p10: 'p10 floor',
                    band_p10_p25: 'p10–p25',
                    band_p25_p50: 'p25–p50',
                    band_p50_p75: 'p50–p75',
                    band_p75_p90: 'p75–p90',
                  }
                  return [fmtGBP(v as number), labels[name] ?? name]
                }}
                labelFormatter={(l) => `Age ${l}`}
              />
              <ReferenceLine
                x={retireAge}
                stroke="#f59e0b"
                strokeDasharray="3 3"
                label={{ value: 'Retire', fill: '#f59e0b', fontSize: 11 }}
              />

              {/* p10 baseline (not stacked — actual value) */}
              <Area
                type="monotone"
                dataKey="band_p10"
                stackId="fan"
                stroke="none"
                fill="transparent"
                name="band_p10"
                legendType="none"
              />
              {/* p10–p25 */}
              <Area
                type="monotone"
                dataKey="band_p10_p25"
                stackId="fan"
                stroke="none"
                fill="#3b82f6"
                fillOpacity={0.12}
                name="band_p10_p25"
                legendType="none"
              />
              {/* p25–p50 */}
              <Area
                type="monotone"
                dataKey="band_p25_p50"
                stackId="fan"
                stroke="none"
                fill="#3b82f6"
                fillOpacity={0.18}
                name="band_p25_p50"
                legendType="none"
              />
              {/* p50–p75 */}
              <Area
                type="monotone"
                dataKey="band_p50_p75"
                stackId="fan"
                stroke="none"
                fill="#3b82f6"
                fillOpacity={0.18}
                name="band_p50_p75"
                legendType="none"
              />
              {/* p75–p90 */}
              <Area
                type="monotone"
                dataKey="band_p75_p90"
                stackId="fan"
                stroke="none"
                fill="#3b82f6"
                fillOpacity={0.12}
                name="band_p75_p90"
                legendType="none"
              />
              {/* Median line */}
              <Area
                type="monotone"
                dataKey="p50"
                stroke="#3b82f6"
                strokeWidth={2.5}
                fill="none"
                dot={false}
                name="p50"
                legendType="none"
              />
            </AreaChart>
          </ResponsiveContainer>

          {/* Band legend */}
          <div className="flex flex-wrap gap-4 mt-3 text-xs text-text-muted">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-blue/50 inline-block" />
              p10–p90 range
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-blue/70 inline-block" />
              p25–p75 range
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 bg-blue-light inline-block" />
              Median (p50)
            </span>
          </div>
        </div>
      )}

      {/* Depletion warning */}
      {data && data.depletion_age_median !== null && (
        <div className="card p-5 border-amber/20 bg-amber/5">
          <h2 className="panel-title mb-2 flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber" aria-hidden />
            Portfolio Depletion Risk
          </h2>
          <p className="text-xs text-text-secondary">
            In the simulations that failed, the median depletion age was{' '}
            <span className="font-bold text-text-primary">{data.depletion_age_median}</span>.{' '}
            {fmtNum(100 - successPct, 0)}% of paths depleted before age {ageTarget}.
          </p>
        </div>
      )}

      {/* Methodology footer */}
      <details className="card p-4 text-xs text-text-secondary">
        <summary className="cursor-pointer text-text-primary font-semibold flex items-center gap-2">
          <Info size={13} aria-hidden />
          Methodology
        </summary>
        <div className="mt-3 space-y-2">
          <p>
            <span className="text-text-primary font-semibold">Monte Carlo method:</span>{' '}
            Each simulation draws an independent annual return from{' '}
            Normal(mean_return, volatility) for every year in the horizon.
            No autocorrelation or fat tails are modelled — returns are i.i.d.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Spending:</span>{' '}
            Spending is held flat in REAL terms — the same purchasing power
            every year. Returns are real (post-inflation) too, so escalating
            the spend as well would double-count inflation. Every figure here
            is in today's pounds.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Success definition:</span>{' '}
            A path "succeeds" if the portfolio value is above zero at age {ageTarget}.
            A path "fails" the moment the balance hits zero.
          </p>
          <p>
            <span className="text-text-primary font-semibold">Fan chart:</span>{' '}
            At every age the percentile bands (p10, p25, p50, p75, p90) are
            computed across all {data?.inputs.num_simulations.toLocaleString() ?? '10,000'} paths.
            The bands widen over time as uncertainty compounds.
          </p>
          <p className="text-text-muted">
            Standard caveats: past volatility is not predictive, real returns
            vary by market cycle, and this tool does not account for state
            pensions, tax wrappers, or spending flexibility. Use alongside
            the deterministic FIRE planner for a complete picture.
          </p>
        </div>
      </details>
      <ScenarioDisclaimerFooter />
    </div>
  )
}

// ── Slider input component ────────────────────────────────────────────────────
function NumberSlider({
  label,
  value,
  onChange,
  min,
  max,
  step,
  format,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step: number
  format: (v: number) => string
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase tracking-wider text-text-muted">{label}</label>
        <span className="text-xs font-bold text-text-primary">{format(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full accent-blue"
        aria-label={label}
      />
    </div>
  )
}
